import { m } from "framer-motion";
import { CSSProperties, ReactNode } from "react";
import { AtlasName, characterOf, preloadAtlases, sprite } from "./models";
import { kartStyle } from "./renderer";
import { ITEMS, ItemKind, Racer } from "./sim";

// The small pieces the kart game's menu and HUD share: faces, item icons,
// stars, the chunky lettering — and the icons, drawn as SVG. Nothing here
// (or anywhere in the game) is an emoji: a racer is drawn as their roster
// character's model and portrait, or their initial.

preloadAtlases();

/** The chunky outlined lettering of the kart games' HUD. */
export const OUTLINE = { WebkitTextStroke: "2px #1a1206", paintOrder: "stroke fill", textShadow: "0 3px 0 rgba(0,0,0,0.35)" } as const;
/** The race HUD's thicker outline, in em so it scales with the text. */
export const HUD_OUTLINE = { WebkitTextStroke: "0.1em #1a1206", paintOrder: "stroke fill", textShadow: "0 0.06em 0 rgba(0,0,0,0.45)" } as const;

/**
 * Chunky HUD lettering: a gradient fill (yellow to orange, by default) inside
 * a thick dark outline with a drop shadow. Two layers, the outline behind —
 * a stroke on gradient-clipped text would eat half of the fill.
 */
export function Chunky({ children, fill = "linear-gradient(#fff3a0, #ffd23a 45%, #ff8c1a)" }: { children: ReactNode; fill?: string }) {
  return (
    <span className="relative inline-block font-display font-extrabold italic">
      <span aria-hidden className="absolute inset-0 text-[#1a1206]" style={HUD_OUTLINE}>
        {children}
      </span>
      {/* Padded out (and pulled back by as much): italic letters lean past their box, and a background is
          only painted inside it, so the fill stopped short of each letter's top right. */}
      <span
        className="relative"
        style={{ background: fill, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", padding: "0.1em 0.25em", margin: "0 -0.25em" }}
      >
        {children}
      </span>
    </span>
  );
}

export const rgbCss = ([r, g, b]: number[]) => `rgb(${r * 255}, ${g * 255}, ${b * 255})`;

export const formatTime = (t: number) => {
  const min = Math.floor(t / 60);
  const sec = (t % 60).toFixed(2).padStart(5, "0");
  return `${min}:${sec}`;
};

/** A member's first letter, for when there's no portrait. */
const initial = (name: string) => Array.from(name.trim())[0]?.toUpperCase() ?? "?";

/**
 * The portrait of the character a racer drives, on their kart's colour —
 * or, with none, their initial on it.
 */
export function Face({ racer }: { racer: Racer }) {
  const char = characterOf(racer);
  const style = kartStyle(racer);
  if (!char) {
    return (
      <span
        aria-hidden
        className="flex w-full h-full items-center justify-center font-display font-extrabold text-[0.9em] leading-none text-white"
        style={{ background: rgbCss(style.body), textShadow: "0 1px 0 rgba(0,0,0,0.5)" }}
      >
        {initial(racer.name)}
      </span>
    );
  }
  return (
    <span className="relative block w-full h-full" style={{ background: `radial-gradient(circle at 50% 35%, ${rgbCss(style.accent)}, ${rgbCss(style.body)} 70%)` }}>
      <Sprite atlas="roster" frame={char} label={racer.name} className="scale-110" />
    </span>
  );
}

/**
 * One image from a texture atlas, filling its box: the atlas scaled so the
 * frame is the box, and shifted to it.
 */
export function Sprite({ atlas, frame, label, className = "" }: { atlas: AtlasName; frame: string; label: string; className?: string }) {
  const s = sprite(atlas, frame);
  if (!s) return <span className="font-mono text-[9px] leading-none text-white text-center">{label}</span>;
  const [x, y, w, h] = s.rect;
  const [W, H] = s.size;
  return (
    <span
      role="img"
      aria-label={label}
      className={`block w-full h-full bg-no-repeat ${className}`}
      style={{
        backgroundImage: `url(${s.url})`,
        backgroundSize: `${(W / w) * 100}% ${(H / h) * 100}%`,
        backgroundPosition: `${(x / (W - w)) * 100}% ${(y / (H - h)) * 100}%`,
      }}
    />
  );
}

/** An item's Blender icon; the rocket has none, so it's drawn. */
export function ItemIcon({ kind }: { kind: ItemKind }) {
  if (kind === "rocket") return <RocketIcon />;
  return <Sprite atlas="hud" frame={`item-${kind}`} label={ITEMS[kind].label} />;
}

/** One to five stars, the earned ones gold — popping in one by one, after a race. */
export function Stars({ count, size, animate = false }: { count: number; size: number; animate?: boolean }) {
  return (
    <span className="flex gap-[1px] leading-none" aria-label={`${count} af 5 stjerner`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <m.span
          key={i}
          initial={animate && i < count ? { scale: 0 } : false}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 500, damping: 14, delay: animate ? 0.6 + i * 0.18 : 0 }}
          style={{ fontSize: size }}
          className={i < count ? "text-[#f5b400]" : "text-paper/25"}
        >
          ★
        </m.span>
      ))}
    </span>
  );
}

// ── Icons ────────────────────────────────────────────────────────────────────

const svg = (children: JSX.Element, size: number | string, style?: CSSProperties) => (
  <svg aria-hidden viewBox="0 0 24 24" width={size} height={size} style={style} className="block flex-shrink-0">
    {children}
  </svg>
);

export const RocketIcon = ({ size = "80%" }: { size?: number | string }) =>
  svg(
    <g transform="rotate(45 12 12)">
      <rect x="11.6" y="15" width="0.8" height="8" fill="#b98a55" />
      <path d="M8.6 9 Q12 -1 15.4 9 Z" fill="#f98b1a" />
      <rect x="8.6" y="9" width="6.8" height="7" fill="#dc2626" />
      <rect x="8.6" y="11.6" width="6.8" height="1.6" fill="#fafafa" />
      <path d="M8.6 14 L6 17 L8.6 16.5 Z M15.4 14 L18 17 L15.4 16.5 Z" fill="#facc15" />
    </g>,
    size,
  );

export const FlagIcon = ({ size = 20 }: { size?: number }) =>
  svg(
    <>
      <rect x="4" y="3" width="1.8" height="19" rx="0.9" fill="#f5f5f5" />
      {[0, 1, 2, 3].flatMap((r) =>
        [0, 1, 2, 3].map((c) => <rect key={`${r}${c}`} x={5.8 + c * 3.6} y={3 + r * 3} width="3.6" height="3" fill={(r + c) % 2 ? "#fafafa" : "#111"} />),
      )}
      <rect x="5.8" y="3" width="14.4" height="12" fill="none" stroke="#111" strokeWidth="0.8" />
    </>,
    size,
  );

export const TrophyIcon = ({ size = 16 }: { size?: number }) =>
  svg(
    <>
      <path d="M7 3h10v6a5 5 0 0 1-10 0z" fill="#facc15" />
      <path d="M7 5H3.5c0 4 1.5 6 4 6.5M17 5h3.5c0 4-1.5 6-4 6.5" fill="none" stroke="#facc15" strokeWidth="1.6" />
      <rect x="10.8" y="13.5" width="2.4" height="4" fill="#eab308" />
      <rect x="7.5" y="17.5" width="9" height="3" rx="1" fill="#a16207" />
      <path d="M9.5 5.5v3.5" stroke="#fff8" strokeWidth="1.2" strokeLinecap="round" />
    </>,
    size,
  );

export const GhostIcon = ({ size = 14 }: { size?: number }) =>
  svg(
    <>
      <path d="M5 21V11a7 7 0 0 1 14 0v10l-2.3-2-2.4 2-2.3-2-2.3 2-2.4-2z" fill="#dff1ff" />
      <circle cx="9.5" cy="11" r="1.4" fill="#123" />
      <circle cx="14.5" cy="11" r="1.4" fill="#123" />
    </>,
    size,
  );

export const SpeakerIcon = ({ muted, size = 22 }: { muted: boolean; size?: number }) =>
  svg(
    <>
      <path d="M3 9.5h4l5-4v13l-5-4H3z" fill="#fff" />
      {muted ? (
        <path d="M15.5 9.5l5 5m0-5l-5 5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      ) : (
        <path d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      )}
    </>,
    size,
  );

export const ChevronLeft = ({ size = 18 }: { size?: number }) =>
  svg(<path d="M15 4l-8 8 8 8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />, size);

export const CheckIcon = ({ size = 16 }: { size?: number }) =>
  svg(<path d="M4 12.5l5 5L20 6.5" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />, size);

export const StopwatchIcon = ({ size = 14 }: { size?: number }) =>
  svg(
    <>
      <circle cx="12" cy="13.5" r="7.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
      <path d="M12 13.5V9M9.5 3h5M12 3v3M18.5 6l1.5 1.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </>,
    size,
  );

export const BoltIcon = ({ size = 14 }: { size?: number }) =>
  svg(<path d="M13.5 2L5 13.5h6L10 22l9-12h-6z" fill="currentColor" />, size);
